import React, { useContext, useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trans } from 'react-i18next';
import { CompanyContext } from 'contexts/CompanyContext';
import { CompanyOnboardingContext } from 'contexts/CompanyOnboardingContext';
import { Avatar, Notification, toast, Upload, Button, Tooltip } from 'components/ui';
import { Loading } from 'components/shared';
import { TbPlus, TbWand, TbPalette, TbPhoto, TbSparkles } from 'react-icons/tb';
import Resizer from "react-image-file-resizer";
import { SketchPicker } from 'react-color';
import { HiX } from 'react-icons/hi';
import { colorIsLight } from 'utils/colorUtils';
import { extractDominantColors } from 'utils/colorUtils';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';
import useDarkMode from 'utils/hooks/useDarkMode';

export default function StepCompanyBrandStyle() {
  const { company } = useContext(CompanyContext);
  const { setCurrentStepData } = useContext(CompanyOnboardingContext);
  const { whitelabel } = useWhitelabel();
  const [isDarkMode] = useDarkMode();
  const [isLoadingLogo, setIsLoadingLogo] = useState(false);
  const [logo, setLogo] = useState(company?.image?.url || '');
  const [openedPicker, setOpenedPicker] = useState(-1);
  const [palettes, setPalettes] = useState(company?.palettes || ['', '', '']);
  const [isLoadingExtractColors, setIsLoadingExtractColors] = useState(false);

  const pickerRef = useRef(null);

  // Update the context with current data
  useEffect(() => {
    setCurrentStepData({
      image: logo ? { url: logo } : undefined,
      palettes
    });
  }, [logo, palettes, setCurrentStepData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setOpenedPicker(-1);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const resizeFile = (file) => {
    return new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        256,
        256,
        file.type.includes("png") ? "PNG" : "JPEG",
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        "base64"
      );
    });
  };

  const onLogoUpload = (file) => {
    setIsLoadingLogo(true);
    resizeFile(file[0])
      .then((data) => {
        setLogo(data);
      })
      .finally(() => {
        setIsLoadingLogo(false);
      });
  };

  const onBeforeLogoUpload = (files) => {
    let valid = true;
    const allowedFileType = ['image/jpeg', 'image/png'];
    for (let file of files) {
      if (!allowedFileType.includes(file.type)) {
        valid = false;
        toast.push(
          <Notification type="error" title="Please upload a .jpeg or .png file" />
        );
      }
    }
    return valid;
  };

  const tooltips = {
    logo: "Upload your company logo",
    primary: "Select your primary brand color",
    secondary: "Select your secondary brand color",
    tertiary: "Select your accent/tertiary color"
  };

  const extractColors = () => {
    if (!logo) return;
    setIsLoadingExtractColors(true);
    extractDominantColors(logo)
      .then((colors) => {
        setPalettes(colors);
      })
      .finally(() => {
        setIsLoadingExtractColors(false);
      });
  };

  return (
    <>
      <div className="flex flex-col gap-4 md:gap-6 flex-1 overflow-y-auto">
        {/* Animated Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="flex justify-center mb-2 md:mb-4"
        >
          <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-2xl md:rounded-3xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shadow-2xl shadow-pink-500/50">
            <TbPalette className="text-3xl md:text-4xl lg:text-5xl text-white" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center"
        >
          <h3 className={`text-2xl md:text-3xl lg:text-4xl font-black mb-2 md:mb-3 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Set Your Brand Style
          </h3>
          <p className={`text-base md:text-lg ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Add your logo and brand colors for consistent visuals
          </p>
        </motion.div>

        {/* Logo Upload Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2 md:space-y-3"
        >
          <div className="flex items-center gap-2">
            <TbPhoto className={`text-lg md:text-xl ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            <label className={`font-bold text-xs md:text-sm ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Company Logo (Optional)
            </label>
          </div>
          
          <div className="flex justify-center">
            <Tooltip title="Upload your company logo">
              <div className={`relative rounded-2xl md:rounded-3xl p-2 md:p-3 group transition-all cursor-pointer ${
                isDarkMode
                  ? 'bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-purple-500'
                  : 'bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 hover:border-purple-500'
              }`}>
                <Upload
                  className="cursor-pointer"
                  onChange={onLogoUpload}
                  showList={false}
                  uploadLimit={1}
                  beforeUpload={onBeforeLogoUpload}
                  accept="image/jpeg, image/png"
                >
                  <div className="flex justify-center">
                    <Avatar
                      size={100}
                      avatarClassName={`!object-contain group-hover:scale-[1.05] transition-transform`}
                      className={`${
                        logo 
                          ? 'bg-transparent' 
                          : isDarkMode
                            ? '!bg-purple-500/20 !text-purple-400'
                            : '!bg-purple-100 !text-purple-600'
                      } rounded-xl md:rounded-2xl md:!w-[120px] md:!h-[120px]`}
                      src={logo}
                      icon={isLoadingLogo ? <Loading type="element" /> : <TbPlus className="text-3xl md:text-4xl" />}
                    />
                  </div>
                </Upload>
                {logo && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1.5 -right-1.5 md:-top-2 md:-right-2"
                  >
                    <Button
                      shape="circle"
                      size="xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLogo('');
                      }}
                      className="!bg-red-500 hover:!bg-red-600 !text-white shadow-lg md:!w-8 md:!h-8"
                      icon={<HiX className="text-sm md:text-lg" />}
                    />
                  </motion.div>
                )}
              </div>
            </Tooltip>
          </div>
        </motion.div>

        {/* AI Extract Colors Button */}
        {logo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="flex justify-center"
          >
            <Button
              variant="solid"
              onClick={extractColors}
              className="!bg-gradient-to-r !from-purple-500 !to-pink-500 hover:!from-purple-600 hover:!to-pink-600 !text-white !shadow-lg hover:!shadow-purple-500/50 !text-xs md:!text-sm"
              size="sm"
              loading={isLoadingExtractColors}
              icon={<TbSparkles className="text-sm md:text-base" />}
            >
              <Trans i18nKey="companyBranding.getColorsFromLogo">Extract Colors with AI</Trans>
            </Button>
          </motion.div>
        )}

        {/* Brand Colors Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-2 md:space-y-3"
        >
          <div className="flex items-center gap-2">
            <TbPalette className={`text-lg md:text-xl ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`} />
            <label className={`font-bold text-xs md:text-sm ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Brand Colors
            </label>
          </div>

          <div className='grid grid-cols-3 gap-3 md:gap-4'>
            {[
              { label: 'Primary', tooltip: 'Your main brand color' },
              { label: 'Secondary', tooltip: 'Supporting brand color' },
              { label: 'Accent', tooltip: 'Highlight/accent color' }
            ].map((colorInfo, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                <Tooltip title={colorInfo.tooltip} wrapperClass="w-full">
                  <div className="space-y-1.5 md:space-y-2 w-full">
                    <div className={`text-[10px] md:text-xs font-semibold text-center ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {colorInfo.label}
                    </div>
                    <div className={`relative rounded-xl md:rounded-2xl p-0.5 md:p-1 group transition-all cursor-pointer ${
                      isDarkMode
                        ? 'bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-purple-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 hover:border-purple-500'
                    }`}>
                      <div
                        onClick={(e) => {
                          setOpenedPicker(index);
                          e.stopPropagation();
                        }}
                        className={`w-full aspect-square flex items-center justify-center rounded-lg md:rounded-xl font-mono text-[9px] md:text-xs transition-transform group-hover:scale-105 ${
                          palettes[index] 
                            ? 'shadow-lg' 
                            : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                        }`}
                        style={{
                          backgroundColor: palettes[index] || '',
                          color: !palettes[index] || colorIsLight(palettes[index]) ? '#666666' : '#ffffff'
                        }}
                      >
                        {palettes[index] || <TbPlus className="text-xl md:text-3xl" />}
                      </div>
                      {palettes[index] && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1"
                        >
                          <Button
                            shape="circle"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newPalettes = [...palettes];
                              newPalettes[index] = '';
                              setPalettes(newPalettes);
                            }}
                            className={`${
                              isDarkMode
                                ? '!bg-gray-700 hover:!bg-gray-600'
                                : '!bg-white hover:!bg-gray-100'
                            } !shadow-lg !w-5 !h-5 md:!w-6 md:!h-6`}
                            icon={<HiX className="text-[10px] md:text-sm" />}
                          />
                        </motion.div>
                      )}
                    </div>
                  </div>
                </Tooltip>

                {/* Color Picker Modal */}
                <AnimatePresence>
                  {openedPicker === index && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setOpenedPicker(-1)}
                      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.9, y: 20 }}
                        ref={pickerRef}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SketchPicker
                          color={palettes[index] || '#ffffff'}
                          onChange={({ hex: color }) => {
                            const newPalettes = [...palettes];
                            newPalettes[index] = color;
                            setPalettes(newPalettes);
                          }}
                        />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className={`p-3 md:p-4 rounded-xl md:rounded-2xl ${
            isDarkMode 
              ? 'bg-pink-500/10 border border-pink-500/20' 
              : 'bg-pink-50 border border-pink-200'
          }`}
        >
          <p className={`text-xs md:text-sm ${
            isDarkMode ? 'text-pink-300' : 'text-pink-700'
          }`}>
            ✨ <strong>Optional Step:</strong> Brand colors help maintain visual consistency across your content. You can skip this and add them later!
          </p>
        </motion.div>
      </div>
    </>
  );
}
